import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { coursesApi, learningPathsApi, orgApi } from '../../api/client';

const emptyForm = {
  title: '',
  description: '',
  isSequential: true,
  isCertification: false,
  mandatoryForScheduling: false,
  certificationExpirationDays: 365,
};

const LearningPathsPage: React.FC<{ mode?: 'manage' | 'assign' | 'progress' }> = ({ mode = 'manage' }) => {
  const [items, setItems] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<any>(emptyForm);
  const [selectedCourses, setSelectedCourses] = useState<any[]>([]);
  const [assignPath, setAssignPath] = useState<any>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [viewing, setViewing] = useState<any>(null);

  const load = async () => {
    try {
      const [p, c, e, a] = await Promise.all([
        learningPathsApi.list(),
        coursesApi.list(),
        orgApi.listEmployees(),
        learningPathsApi.listAssignments(),
      ]);
      setItems(p);
      setCourses(c);
      setEmployees(e);
      setAssignments(a);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (publish: boolean) => {
    try {
      setSubmitting(true);
      setError(null);
      const payload = {
        title: form.title,
        description: form.description,
        isSequential: !!form.isSequential,
        isCertification: !!form.isCertification,
        mandatoryForScheduling: !!form.mandatoryForScheduling,
        certificationExpirationDays: form.isCertification ? Number(form.certificationExpirationDays) || 365 : null,
        status: publish ? 'published' : 'draft',
        courses: selectedCourses.map((item, index) => ({
          courseId: item.courseId,
          order: index + 1,
          isRequired: item.isRequired,
        })),
      };
      if (form.id) await learningPathsApi.update(form.id, payload);
      else await learningPathsApi.create(payload);
      setCreating(false);
      setForm(emptyForm);
      setSelectedCourses([]);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const assign = async () => {
    try {
      setSubmitting(true);
      for (const employeeId of selectedEmployeeIds) {
        await learningPathsApi.assign(assignPath.id, { employeeId });
      }
      setAssignPath(null);
      setSelectedEmployeeIds([]);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (creating) {
    const unselected = courses.filter((c) => !selectedCourses.some((s) => s.courseId === c.id));
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{form.id ? 'Edit Learning Path' : 'Create Learning Path'}</Typography>
          <Button variant="outlined" onClick={() => setCreating(false)}>Cancel</Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <TextField fullWidth label="Learning Path Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mb: 2 }} />
            <TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={3} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: 'column' }}>
              <FormControlLabel control={<Switch checked={!!form.isSequential} onChange={(e) => setForm({ ...form, isSequential: e.target.checked })} />} label="Sequential (courses must be completed in order)" />
              <FormControlLabel control={<Switch checked={!!form.mandatoryForScheduling} onChange={(e) => setForm({ ...form, mandatoryForScheduling: e.target.checked })} />} label="Mandatory for Scheduling" />
              <FormControlLabel control={<Switch checked={!!form.isCertification} onChange={(e) => setForm({ ...form, isCertification: e.target.checked })} />} label="Certification (expires and requires recertification)" />
              {form.isCertification && (
                <TextField type="number" label="Certification valid for (days)" value={form.certificationExpirationDays} onChange={(e) => setForm({ ...form, certificationExpirationDays: e.target.value })} size="small" sx={{ maxWidth: 200 }} helperText="e.g. 365 for 1 year" />
              )}
            </Box>
            {!form.isSequential && <Alert severity="info" sx={{ mb: 2 }}>Flexible mode: Employees can take courses in any order.</Alert>}
            {form.mandatoryForScheduling && <Alert severity="warning" sx={{ mb: 2 }}>This learning path is mandatory for scheduling eligibility. Employees must complete this path and have banking information on file to be eligible for scheduling.</Alert>}
            {form.isCertification && <Alert severity="info">This path will expire after {form.certificationExpirationDays} days. Employees will receive reminders before expiration and will be re-assigned automatically when it lapses.</Alert>}
          </CardContent>
        </Card>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Add Courses</Typography>
            {unselected.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel>Select a course to add</InputLabel>
                <Select value="" label="Select a course to add" onChange={(e) => {
                  const course = courses.find((c) => c.id === e.target.value);
                  if (course) setSelectedCourses((prev) => [...prev, { courseId: course.id, course, isRequired: true }]);
                }}>
                  {unselected.map((course) => (
                    <MenuItem key={course.id} value={course.id}>{course.title}{course.duration ? ` (${course.duration})` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Alert severity="info">All available courses have been added to this learning path.</Alert>
            )}
          </CardContent>
        </Card>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Course Sequence ({selectedCourses.length} course{selectedCourses.length === 1 ? '' : 's'})</Typography>
            {selectedCourses.length === 0 ? (
              <Alert severity="warning">No courses added yet. Add at least one course to create the learning path.</Alert>
            ) : (
              <List>
                {selectedCourses.map((item, index) => (
                  <Paper key={item.courseId} elevation={1} sx={{ mb: 1 }}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight="medium">{index + 1}. {item.course.title}</Typography>
                            <Chip
                              label={item.isRequired ? 'Required' : 'Optional'}
                              size="small"
                              color={item.isRequired ? 'primary' : 'default'}
                              onClick={() => setSelectedCourses((prev) => prev.map((c, i) => i === index ? { ...c, isRequired: !c.isRequired } : c))}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Box>
                        }
                        secondary={item.course.description}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" color="error" onClick={() => setSelectedCourses((prev) => prev.filter((_, i) => i !== index))}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" disabled={submitting} onClick={() => create(false)}>Save as Draft</Button>
          <Button variant="contained" disabled={submitting || selectedCourses.length === 0 || !form.title} onClick={() => create(true)}>
            {submitting ? <CircularProgress size={24} /> : form.id ? 'Save Changes' : 'Publish Learning Path'}
          </Button>
        </Box>
      </Box>
    );
  }

  if (assignPath) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={600}>Assign Learning Path</Typography>
          <Button variant="outlined" onClick={() => setAssignPath(null)}>Cancel</Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{assignPath.title}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>Select employees to assign this learning path.</Typography>
            {employees.map((employee) => (
              <Box key={employee.id} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: selectedEmployeeIds.includes(employee.id) ? '#f0f8ff' : 'white' }}>
                <input
                  type="checkbox"
                  checked={selectedEmployeeIds.includes(employee.id)}
                  onChange={(e) => setSelectedEmployeeIds((ids) => e.target.checked ? [...ids, employee.id] : ids.filter((id) => id !== employee.id))}
                  style={{ marginRight: 12, marginTop: 4 }}
                />
                <Box>
                  <Typography fontWeight={700}>{employee.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{employee.email}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
        <Button variant="contained" disabled={submitting || selectedEmployeeIds.length === 0} onClick={assign}>
          {submitting ? 'Assigning...' : 'Assign Learning Path'}
        </Button>
      </Box>
    );
  }

  const startEdit = (path: any) => {
    setForm({
      id: path.id,
      title: path.title || '',
      description: path.description || '',
      isSequential: path.isSequential !== false,
      isCertification: !!path.isCertification,
      mandatoryForScheduling: !!path.mandatoryForScheduling,
      certificationExpirationDays: path.certificationExpirationDays || 365,
    });
    setSelectedCourses((path.courses || []).map((item: any) => ({
      courseId: item.courseId,
      course: item.course,
      isRequired: item.isRequired !== false,
    })));
    setCreating(true);
  };

  const archivePath = async (path: any) => {
    const nextArchived = !path.isArchived;
    const action = nextArchived ? 'archive' : 'restore';
    if (!window.confirm(`${action[0].toUpperCase()}${action.slice(1)} "${path.title}"?`)) return;
    try {
      await learningPathsApi.update(path.id, { isArchived: nextArchived });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  const pathRow = (path: any, archived: boolean) => {
    const courseCount = path.courses?.length || 0;
    return (
      <TableRow key={path.id} hover={!archived} sx={{ opacity: archived ? 0.7 : 1, bgcolor: archived ? 'grey.50' : 'inherit' }}>
        <TableCell>
          <Typography fontWeight={600}>{path.title}</Typography>
          {path.description && (
            <Typography variant="body2" color="text.secondary">{path.description}</Typography>
          )}
        </TableCell>
        <TableCell><Chip label={`v${path.version || 1}`} size="small" variant="outlined" /></TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip label={path.status === 'published' ? 'Published' : 'Draft'} color={path.status === 'published' ? 'success' : 'default'} size="small" />
            {path.mandatoryForScheduling && <Chip label="Mandatory for Scheduling" size="small" color="primary" variant="outlined" />}
            {path.isCertification && (
              <Chip
                label={path.certificationExpirationDays ? `Certification (${path.certificationExpirationDays}d)` : 'Certification'}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
        </TableCell>
        <TableCell>{courseCount} course{courseCount === 1 ? '' : 's'}</TableCell>
        <TableCell>
          <Chip label={path.isSequential === false ? 'Flexible' : 'Sequential'} size="small" variant="outlined" />
        </TableCell>
        <TableCell>{path.createdAt ? new Date(path.createdAt).toLocaleDateString('en-GB') : '—'}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex' }}>
            <IconButton size="small" title="View" onClick={() => setViewing(path)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
            {mode === 'manage' ? (
              <>
                <IconButton size="small" title="Edit" color="primary" onClick={() => startEdit(path)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" title={archived ? 'Restore' : 'Archive'} onClick={() => archivePath(path)}>
                  {archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                </IconButton>
              </>
            ) : (
              <Button size="small" onClick={() => { setAssignPath(path); setSelectedEmployeeIds([]); }}>Assign</Button>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const activePaths = items.filter((path) => !path.isArchived);
  const archivedPaths = items.filter((path) => path.isArchived);
  const title = mode === 'assign' ? 'Assign Learning Path' : mode === 'progress' ? 'Learning Path Progress' : 'Learning Path Management';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" fontWeight={700}>{title}</Typography>
        {mode === 'manage' && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm(emptyForm); setSelectedCourses([]); setCreating(true); }}>
            Create New Learning Path
          </Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {mode !== 'progress' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>Active Learning Paths ({activePaths.length})</Typography>
            <FormControlLabel
              control={<Switch checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
              label="Show Archived Paths"
            />
          </Box>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {['Title', 'Version', 'Status', 'Courses', 'Type', 'Created', 'Actions'].map((column) => (
                    <TableCell key={column}><strong>{column}</strong></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {activePaths.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No active learning paths</TableCell>
                  </TableRow>
                ) : activePaths.map((path) => pathRow(path, false))}
              </TableBody>
            </Table>
          </TableContainer>
          {showArchived && (
            <>
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                Archived Learning Paths ({archivedPaths.length})
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['Title', 'Version', 'Status', 'Courses', 'Type', 'Created', 'Actions'].map((column) => (
                        <TableCell key={column}><strong>{column}</strong></TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {archivedPaths.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">No archived learning paths</TableCell>
                      </TableRow>
                    ) : archivedPaths.map((path) => pathRow(path, true))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {(mode === 'progress' || mode === 'assign') && (
        <>
          <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>Assigned paths</Typography>
          {assignments.map((a) => (
            <Box key={a.id} sx={{ p: '1.5rem', mb: 1, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>{a.employee?.name} → {a.learningPath?.title}</Typography>
              <Typography variant="body2" color="text.secondary">{a.status} {a.certificationStatus ? `· ${a.certificationStatus}` : ''}</Typography>
            </Box>
          ))}
        </>
      )}

      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{viewing?.title}</DialogTitle>
        <DialogContent>
          {viewing?.description && <Typography color="text.secondary" sx={{ mb: 2 }}>{viewing.description}</Typography>}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`v${viewing?.version || 1}`} size="small" variant="outlined" />
            <Chip label={viewing?.isSequential === false ? 'Flexible' : 'Sequential'} size="small" variant="outlined" />
            <Chip label={viewing?.status === 'published' ? 'Published' : 'Draft'} color={viewing?.status === 'published' ? 'success' : 'default'} size="small" />
          </Box>
          <Typography fontWeight={600} sx={{ mb: 1 }}>Courses</Typography>
          {(viewing?.courses || []).length === 0 && <Typography color="text.secondary">No courses in this path.</Typography>}
          {(viewing?.courses || []).map((item: any, index: number) => (
            <Typography key={item.id || index} sx={{ mb: 0.5 }}>
              {index + 1}. {item.course?.title || 'Course'}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearningPathsPage;
